/**
 * Spin Banner — Spinnio bridge state machine + static wheel + rules dialog.
 *
 * Banner pointer stays static (avoids prize mismatch with Spinnio).
 * States (class checks use previous + current snapshots where noted):
 * - pluginReady: nested has trigger-button-box + loaded
 * - dialogActive: nested has ab-active (prev AND curr)
 * - canReopen: closed without prize — prev dialogActive, curr only trigger-button-box
 *              (or prev+curr both only trigger-button-box after a dialog session)
 * - hasSpun: sessionStorage after manage-widget success + button.ab-spin-triger
 */
class SpinBanner extends HTMLElement {
  static SPINNIO_ROOT_ID = 'Spinnio_root';
  static SPINNIO_TRIGGER = '#Spinnio_root .trigger-button-box';
  static SPINNIO_NESTED = '#Spinnio_root > div > div';
  static SPINNIO_SPIN_BTN = '#Spinnio_root button.ab-spin-triger';
  static MANAGE_WIDGET_URL = 'spinwheel.engage2convert.co/manage-widget';
  /** Only set after manage-widget success during a user-started spin. */
  static HAS_SPUN_KEY = 'spinBannerHasClaimedPrize';
  /** Legacy key wrongly set when `ab-spin-triger` existed before any spin. */
  static LEGACY_HAS_SPUN_KEY = 'spinBannerSpinnioHasSpun';
  static TRIGGER_RETRY_MS = 100;
  static TRIGGER_TIMEOUT_MS = 5000;

  connectedCallback() {
    if (this.dataset.spinReady === 'true') return;
    this.dataset.spinReady = 'true';

    this.trigger = this.querySelector('[data-spin-trigger]');
    this.rulesOpen = this.querySelector('[data-spin-rules-open]');
    this.rulesDialog = this.querySelector('[data-spin-rules-dialog]');
    this.rulesClose = this.querySelector('[data-spin-rules-close]');

    this.isOpening = false;

    this.pluginReady = false;
    this.dialogActive = false;
    this.hadDialogSession = false;
    this.canReopen = false;
    this.userStartedSpin = false;
    this.clearLegacyHasSpunFlag();
    this.hasSpun = this.readHasSpun();
    this.prevNestedClasses = '';
    this.currNestedClasses = '';

    this.spinnioObserver = null;
    this.spinnioRootWaiter = null;
    this.resourceObserver = null;
    this.originalFetch = null;
    this.originalXhrOpen = null;
    this.originalXhrSend = null;

    this.onSpinClick = this.onSpinClick.bind(this);
    this.onRulesOpen = this.onRulesOpen.bind(this);
    this.onRulesClose = this.onRulesClose.bind(this);
    this.onDialogClick = this.onDialogClick.bind(this);
    this.onSpinnioMutation = this.onSpinnioMutation.bind(this);

    if (this.trigger && this.hasAttribute('data-enable-spin')) {
      this.trigger.addEventListener('click', this.onSpinClick);
    }

    if (this.rulesOpen && this.rulesDialog) {
      this.rulesOpen.addEventListener('click', this.onRulesOpen);
    }
    if (this.rulesClose && this.rulesDialog) {
      this.rulesClose.addEventListener('click', this.onRulesClose);
    }
    if (this.rulesDialog) {
      this.rulesDialog.addEventListener('click', this.onDialogClick);
    }

    this.syncSpinButton();
    this.observeSpinnioRoot();
    this.watchManageWidgetRequests();
  }

  disconnectedCallback() {
    if (this.trigger) this.trigger.removeEventListener('click', this.onSpinClick);
    if (this.rulesOpen) this.rulesOpen.removeEventListener('click', this.onRulesOpen);
    if (this.rulesClose) this.rulesClose.removeEventListener('click', this.onRulesClose);
    if (this.rulesDialog) this.rulesDialog.removeEventListener('click', this.onDialogClick);

    this.disconnectSpinnioObservers();
    this.unwatchManageWidgetRequests();
  }

  /** Drop the buggy legacy flag that marked users as spun on plugin mount. */
  clearLegacyHasSpunFlag() {
    try {
      window.sessionStorage.removeItem(SpinBanner.LEGACY_HAS_SPUN_KEY);
    } catch {
      /* ignore */
    }
  }

  /** @returns {boolean} */
  readHasSpun() {
    try {
      return window.sessionStorage.getItem(SpinBanner.HAS_SPUN_KEY) === '1';
    } catch {
      return false;
    }
  }

  markHasSpun() {
    this.hasSpun = true;
    this.canReopen = false;
    this.userStartedSpin = false;
    try {
      window.sessionStorage.setItem(SpinBanner.HAS_SPUN_KEY, '1');
    } catch {
      /* ignore quota / private mode */
    }
    this.syncSpinButton();
  }

  /**
   * Enable SPIN only when plugin ready, dialog closed, not already spun / opening.
   */
  syncSpinButton() {
    if (!this.trigger) return;

    const isEnabled =
      this.hasAttribute('data-enable-spin') &&
      !this.hasSpun &&
      this.pluginReady &&
      !this.isOpening &&
      !this.dialogActive;

    this.trigger.disabled = !isEnabled;
    this.trigger.setAttribute('aria-disabled', isEnabled ? 'false' : 'true');
    this.trigger.classList.toggle('is-disabled', !isEnabled);
  }

  /**
   * @param {string} className
   * @returns {string[]}
   */
  parseClasses(className) {
    return String(className || '')
      .split(/\s+/)
      .filter(Boolean);
  }

  /**
   * @param {string} className
   * @returns {boolean}
   */
  matchesPluginReady(className) {
    const list = this.parseClasses(className);
    return list.includes('trigger-button-box') && list.includes('loaded');
  }

  /**
   * @param {string} className
   * @returns {boolean}
   */
  matchesDialogActive(className) {
    return this.parseClasses(className).includes('ab-active');
  }

  /**
   * Closed giftbox: only `trigger-button-box`.
   * @param {string} className
   * @returns {boolean}
   */
  matchesClosedGiftOnly(className) {
    const list = this.parseClasses(className);
    return list.length === 1 && list[0] === 'trigger-button-box';
  }

  /**
   * Prefer dialog node when active, otherwise giftbox trigger node.
   * @returns {HTMLElement|null}
   */
  getPrimaryNested() {
    const nodes = [...document.querySelectorAll(SpinBanner.SPINNIO_NESTED)];
    if (!nodes.length) return null;

    const byActive = nodes.find((node) => this.parseClasses(node.className).includes('ab-active'));
    if (byActive) return byActive;

    const byTrigger = nodes.find((node) =>
      this.parseClasses(node.className).includes('trigger-button-box')
    );
    if (byTrigger) return byTrigger;

    return nodes[0];
  }

  /**
   * Aggregate class string used for state machine (dialog wins over giftbox).
   * @returns {string}
   */
  getNestedStateClassName() {
    const nodes = [...document.querySelectorAll(SpinBanner.SPINNIO_NESTED)];
    if (!nodes.length) return '';

    const active = nodes.find((node) => this.parseClasses(node.className).includes('ab-active'));
    if (active) return active.className || '';

    const trigger = nodes.find((node) =>
      this.parseClasses(node.className).includes('trigger-button-box')
    );
    if (trigger) return trigger.className || '';

    return nodes[0].className || '';
  }

  observeSpinnioRoot() {
    const root = document.getElementById(SpinBanner.SPINNIO_ROOT_ID);
    if (root) {
      this.attachSpinnioObserver(root);
      return;
    }

    this.spinnioRootWaiter = new MutationObserver(() => {
      const nextRoot = document.getElementById(SpinBanner.SPINNIO_ROOT_ID);
      if (!nextRoot) return;
      this.spinnioRootWaiter.disconnect();
      this.spinnioRootWaiter = null;
      this.attachSpinnioObserver(nextRoot);
    });

    this.spinnioRootWaiter.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * @param {HTMLElement} root
   */
  attachSpinnioObserver(root) {
    if (this.spinnioObserver) {
      this.spinnioObserver.disconnect();
    }

    this.spinnioObserver = new MutationObserver(this.onSpinnioMutation);
    this.spinnioObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    this.processSpinnioNestedState();
  }

  disconnectSpinnioObservers() {
    if (this.spinnioObserver) {
      this.spinnioObserver.disconnect();
      this.spinnioObserver = null;
    }
    if (this.spinnioRootWaiter) {
      this.spinnioRootWaiter.disconnect();
      this.spinnioRootWaiter = null;
    }
  }

  onSpinnioMutation() {
    this.processSpinnioNestedState();
  }

  processSpinnioNestedState() {
    const nextClasses = this.getNestedStateClassName();

    this.prevNestedClasses = this.currNestedClasses;
    this.currNestedClasses = nextClasses;

    const prev = this.prevNestedClasses;
    const curr = this.currNestedClasses;

    console.log('[spin-banner] Spinnio nested classes', {
      prev,
      curr,
      all: [...document.querySelectorAll(SpinBanner.SPINNIO_NESTED)].map((n) => n.className || ''),
    });

    /* 1) Plugin ready — first time nested has trigger-button-box + loaded */
    if (!this.pluginReady && this.matchesPluginReady(curr)) {
      this.pluginReady = true;
    }

    /* 3) Dialog active — prev AND curr both include ab-active */
    this.dialogActive = this.matchesDialogActive(prev) && this.matchesDialogActive(curr);
    if (this.dialogActive) {
      this.hadDialogSession = true;
      this.canReopen = false;
    }

    /* 4) Closed without prize — require prev+curr class conditions */
    if (!this.hasSpun) {
      const closedTransition =
        this.matchesDialogActive(prev) && this.matchesClosedGiftOnly(curr);
      const closedStable =
        this.hadDialogSession &&
        this.matchesClosedGiftOnly(prev) &&
        this.matchesClosedGiftOnly(curr);

      if (closedTransition || closedStable) {
        this.canReopen = true;
        this.dialogActive = false;
      }
    }

    this.syncSpinButton();
  }

  watchManageWidgetRequests() {
    if (typeof PerformanceObserver !== 'undefined') {
      try {
        this.resourceObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (String(entry.name || '').includes(SpinBanner.MANAGE_WIDGET_URL)) {
              this.onManageWidgetDetected();
            }
          });
        });
        /* buffered:false — avoid treating prior page resource entries as a new claim */
        this.resourceObserver.observe({ type: 'resource', buffered: false });
      } catch {
        this.resourceObserver = null;
      }
    }

    this.originalFetch = window.fetch;
    if (typeof this.originalFetch === 'function') {
      const self = this;
      window.fetch = async function spinBannerFetch(...args) {
        const response = await self.originalFetch.apply(this, args);
        const url = self.resolveRequestUrl(args[0]);
        if (url.includes(SpinBanner.MANAGE_WIDGET_URL) && response && response.ok) {
          self.onManageWidgetDetected();
        }
        return response;
      };
    }

    this.originalXhrOpen = XMLHttpRequest.prototype.open;
    this.originalXhrSend = XMLHttpRequest.prototype.send;
    const self = this;

    XMLHttpRequest.prototype.open = function spinBannerXhrOpen(method, url, ...rest) {
      this.__spinBannerUrl = String(url || '');
      return self.originalXhrOpen.call(this, method, url, ...rest);
    };

    XMLHttpRequest.prototype.send = function spinBannerXhrSend(...args) {
      this.addEventListener('load', function onLoad() {
        if (
          String(this.__spinBannerUrl || '').includes(SpinBanner.MANAGE_WIDGET_URL) &&
          this.status >= 200 &&
          this.status < 300
        ) {
          self.onManageWidgetDetected();
        }
      });
      return self.originalXhrSend.apply(this, args);
    };
  }

  unwatchManageWidgetRequests() {
    if (this.resourceObserver) {
      this.resourceObserver.disconnect();
      this.resourceObserver = null;
    }
    if (this.originalFetch) {
      window.fetch = this.originalFetch;
      this.originalFetch = null;
    }
    if (this.originalXhrOpen) {
      XMLHttpRequest.prototype.open = this.originalXhrOpen;
      this.originalXhrOpen = null;
    }
    if (this.originalXhrSend) {
      XMLHttpRequest.prototype.send = this.originalXhrSend;
      this.originalXhrSend = null;
    }
  }

  /**
   * @param {RequestInfo|URL} input
   * @returns {string}
   */
  resolveRequestUrl(input) {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.href;
    if (input && typeof input === 'object' && 'url' in input) return String(input.url || '');
    return '';
  }

  onManageWidgetDetected() {
    /* Only count claims after the user clicked SPIN NOW (plugin may call this URL on load). */
    if (this.hasSpun || !this.userStartedSpin) return;

    const tryMark = () => {
      if (this.hasSpun) return true;
      const spinBtn = document.querySelector(SpinBanner.SPINNIO_SPIN_BTN);
      if (!spinBtn) return false;
      this.markHasSpun();
      return true;
    };

    if (tryMark()) return;

    [100, 300, 800, 1500].forEach((delay) => {
      window.setTimeout(() => {
        if (!this.hasSpun && this.userStartedSpin) tryMark();
      }, delay);
    });
  }

  /**
   * @param {MouseEvent} event
   */
  onSpinClick(event) {
    if (
      !this.hasAttribute('data-enable-spin') ||
      !this.trigger ||
      this.trigger.disabled ||
      this.hasSpun ||
      !this.pluginReady ||
      this.isOpening ||
      this.dialogActive
    ) {
      return;
    }

    event.preventDefault();

    const shouldReopen = this.canReopen;
    this.canReopen = false;
    this.userStartedSpin = true;
    this.isOpening = true;
    this.syncSpinButton();

    if (shouldReopen) {
      this.reopenSpinnioGiftbox();
      return;
    }

    this.clickSpinnioTrigger();
  }

  /**
   * Closed-without-prize reopen: add `loaded`, then click giftbox.
   */
  reopenSpinnioGiftbox() {
    const nested = this.getPrimaryNested();
    if (nested) {
      nested.classList.add('loaded');
    }
    this.clickSpinnioTrigger();
  }

  /**
   * @returns {Promise<boolean>}
   */
  async clickSpinnioTrigger() {
    const startedAt = Date.now();

    while (Date.now() - startedAt < SpinBanner.TRIGGER_TIMEOUT_MS) {
      const trigger = document.querySelector(SpinBanner.SPINNIO_TRIGGER);
      if (trigger instanceof HTMLElement) {
        trigger.click();
        this.isOpening = false;
        this.syncSpinButton();
        return true;
      }

      await new Promise((resolve) => {
        window.setTimeout(resolve, SpinBanner.TRIGGER_RETRY_MS);
      });
    }

    console.warn('[spin-banner] Spinnio trigger not found:', SpinBanner.SPINNIO_TRIGGER);
    this.isOpening = false;
    this.syncSpinButton();
    return false;
  }

  onRulesOpen() {
    if (this.rulesDialog && typeof this.rulesDialog.showModal === 'function') {
      this.rulesDialog.showModal();
    }
  }

  onRulesClose() {
    if (this.rulesDialog) this.rulesDialog.close();
  }

  /**
   * @param {MouseEvent} event
   */
  onDialogClick(event) {
    if (event.target === this.rulesDialog) {
      this.rulesDialog.close();
    }
  }
}

if (!customElements.get('spin-banner')) {
  customElements.define('spin-banner', SpinBanner);
}
